import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import carousalIndicator from '@/assets/svg/static/carousal-indicator.svg';
import type { SimilarProductImageCarouselProps } from './SimilarProductImageCarousal.types';
import styles from './SimilarProductImageCarousel.module.css';
import SimilarProductImageItem from './SimilarProductImageItem';

const SimilarProductImageCarousel = ({
  imageUrls,
  focusedImage,
  isGeneratingBoxes,
  boxes,
  focusedBox,
  onBoxFocus,
  onBoxResize,
  onBoxResizeStop,
  onImageFocusChange,
}: SimilarProductImageCarouselProps): JSX.Element => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [centeredImageIndex, setCenteredImageIndex] =
    useState<number>(focusedImage);
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    onImageFocusChange(centeredImageIndex);
  }, [centeredImageIndex, onImageFocusChange]);

  const updateCenterIndex = useCallback(() => {
    if (!carouselRef.current) return;

    const container = carouselRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let closestIndex = -1;
    let minDiff = Infinity;

    const imageItems = carouselRef.current.querySelectorAll('.carousel-item');

    imageItems.forEach((item, index) => {
      if (item) {
        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.left + itemRect.width / 2;
        const diff = Math.abs(itemCenter - containerCenter);

        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = index;
        }
      }
    });

    if (closestIndex !== centeredImageIndex) {
      setCenteredImageIndex(closestIndex);
    }
  }, [centeredImageIndex]);

  // Add scroll event listener
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = (): void => updateCenterIndex();

    const handleResize = (): void => updateCenterIndex();

    carousel.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    // Initial check
    handleScroll();

    return () => {
      carousel.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [updateCenterIndex]);

  useEffect(() => {
    const scrollToCenter = (): void => {
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
      const carousel = carouselRef.current;
      if (!carousel) return;
      resizeTimeout.current = setTimeout(() => {
        const items = carousel.querySelectorAll('.carousel-item');
        const focused = items[focusedImage];
        if (focused && typeof focused.scrollIntoView === 'function') {
          focused.scrollIntoView({
            behavior: 'auto',
            inline: 'center',
            block: 'nearest',
          });
        }
      }, 400);
    };

    // Center on mount and when focusedImage changes
    scrollToCenter();

    // Center on resize
    window.addEventListener('resize', scrollToCenter);
    return () => window.removeEventListener('resize', scrollToCenter);
  }, [focusedImage]);

  return (
    <div className={styles.container}>
      <div className={styles.carouselContainer}>
        <div
          ref={carouselRef}
          className={`${styles.carousel} ${imageUrls.length === 1 ? styles.singleImage : styles.multipleImages}`}
        >
          {imageUrls.map((imageUrl, index) => (
            <div key={index} className={`${styles.carouselItem} carousel-item`}>
              <SimilarProductImageItem
                imageUrl={imageUrl}
                isActive={index === focusedImage}
                isGeneratingBoxes={index === focusedImage && isGeneratingBoxes}
                boxes={index === focusedImage ? boxes : undefined}
                focusedBox={index === focusedImage ? focusedBox : null}
                onBoxFocus={index === focusedImage ? onBoxFocus : undefined}
                onBoxResize={index === focusedImage ? onBoxResize : undefined}
                onBoxResizeStop={
                  index === focusedImage ? onBoxResizeStop : undefined
                }
              />
            </div>
          ))}
        </div>

        {imageUrls && imageUrls.length !== 0 && imageUrls.length !== 1 && (
          <div className={styles.dotsContainer}>
            {imageUrls.map((_, index) => {
              return index === focusedImage ? (
                <div key={index} className={styles.focusIndicator}>
                  <img
                    src={carousalIndicator}
                    alt="S"
                    className={styles.indicator}
                  />
                </div>
              ) : (
                <div key={index} className={styles.dots} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimilarProductImageCarousel;
